import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Table } from "../../../../component/table/Table";
import { inventorySparesLotListTableColumnsConfig } from "./inventorySparesLotListTableColumnDef";
import classes from "./inventorySparesLotListTablePage.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  selectInventorySpareLotFilterState,
  setInventorySpareLotListCursor,
} from "../../../../store/inventory/inventorySpareLotListSlice";
import {
  makeInventorySpareLotSelectors,
  makeSelectInventorySpareLotPagination,
  useGetInventorySpareLotListQuery,
} from "../../../../services/inventory/spares/inventorySpareLotListApiSlice";

/* 🔥 IMPORTANT: import this */
import { extractApiFilters } from "../../../../services/inventory/spares/inventorySpareLotListApiSlice";

export const InventorySparesLotListTablePage = () => {
  const navigate = useNavigate();
  const { inventoryId } = useParams();
  const dispatch = useDispatch();
  const bottomRef = useRef(null);

  const filters = useSelector(selectInventorySpareLotFilterState);

  /* ✅ VERY IMPORTANT: make filters match selector logic */
  const activeFilters = useMemo(
    () => extractApiFilters(filters),
    [filters]
  );

  const { isLoading, isError } = useGetInventorySpareLotListQuery(
    {
      inventory_id: inventoryId,
      cursor: filters.cursor ?? 0,
      filters: activeFilters, // ✅ FIXED
    },
    {
      skip: !inventoryId,
      refetchOnMountOrArgChange: true,
    }
  );

  const lotSelectors = useMemo(
    () => makeInventorySpareLotSelectors(inventoryId),
    [inventoryId]
  );

  const pagination = useSelector(
    makeSelectInventorySpareLotPagination(inventoryId)
  );

  const tableData = useSelector(lotSelectors.selectAll);

  const loadMore = useCallback(() => {
    if (!pagination.hasMore || pagination.nextCursor == null) return;
    dispatch(setInventorySpareLotListCursor(pagination.nextCursor));
  }, [dispatch, pagination]);

  useEffect(() => {
    if (!pagination.hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    if (bottomRef.current) observer.observe(bottomRef.current);

    return () => observer.disconnect();
  }, [loadMore, pagination.hasMore]);

  const handleNavigateToLotDetail = useCallback(
    (lotItemId) => {
      navigate(`/inventory/spares/${inventoryId}/${lotItemId}`);
    },
    [inventoryId, navigate]
  );

  const columnDefs = useMemo(
    () => inventorySparesLotListTableColumnsConfig(handleNavigateToLotDetail),
    [handleNavigateToLotDetail]
  );

  if (isLoading && tableData.length === 0) {
    return <div>Loading...</div>;
  }

  if (isError && tableData.length === 0) {
    return <div>Error loading lots</div>;
  }

  return (
    <>
      <Table data={tableData} columns={columnDefs} />

      <div ref={bottomRef} className={classes.loaderContainer}>
        {isLoading ? (
          <div className={classes.spinner}></div>
        ) : !pagination.hasMore && tableData.length > 0 ? (
          <div className={classes.endMessage}>All lots loaded</div>
        ) : null}
      </div>
    </>
  );
};